import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Screen, TopBar, Logo, ScrollArea, BottomBar, PrimaryButton, TextField } from "@/components/jk/ui";
import api from "@/lib/api";

/**
 * Screen 2 - Email OTP Entry
 * Logo left + help icon right. Editorial heading, email field. CTA pinned bottom.
 */
const EmailOtpEntry = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const handleSend = async () => {
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email: email.trim().toLowerCase() });
      navigate("/verify", {
        state: {
          email: email.trim().toLowerCase(),
          devCode: data.dev_code,
          emailFailed: data.email_failed,
          cooldown: data.cooldown,
          provider: data.provider,
        },
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Screen testid="email-entry-screen">
      <TopBar
        left={<Logo />}
        right={null}
      />

      <ScrollArea className="px-6 pt-12">
        <h1 data-testid="email-entry-heading" className="text-[34px] font-bold leading-[1.05] tracking-[-0.02em] text-[#111111]">
          Enter your email
        </h1>
        <p className="mt-3 text-[14px] leading-relaxed text-[#666666]">
          We&apos;ll send you a verification code to sign in securely.
        </p>

        <div className="mt-8">
          <TextField
            testid="email-input"
            label="Email address"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            error={error}
          />
        </div>
      </ScrollArea>

      <BottomBar>
        <PrimaryButton
          testid="send-code-button"
          onClick={handleSend}
          disabled={loading}
          icon={!loading && <ArrowRight size={18} strokeWidth={2} />}
        >
          {loading ? "Sending…" : "Send code"}
        </PrimaryButton>
        <p className="mt-4 text-center text-[12px] leading-relaxed text-[#666666]">
          By continuing, you agree to our{" "}
          <a href="https://jothidakural.com/terms.html" data-testid="terms-link" target="_blank" rel="noreferrer" className="font-medium text-[#111111] underline underline-offset-2">
            Terms of Service
          </a>
        </p>
      </BottomBar>
    </Screen>
  );
};

export default EmailOtpEntry;
